import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon issue with bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Separate component to handle map flying/fitting
function FitBoundsController({ geoJsonData }) {
  const map = useMap();

  useEffect(() => {
    if (geoJsonData && geoJsonData.features && geoJsonData.features.length > 0) {
      try {
        const layer = L.geoJSON(geoJsonData);
        const bounds = layer.getBounds();
        if (bounds.isValid()) {
          map.fitBounds(bounds, { padding: [30, 30], maxZoom: 12 });
        }
      } catch (e) {
        console.warn('Could not fit bounds:', e);
      }
    }
  }, [geoJsonData, map]);

  return null;
}

const DISTRICT_COLORS = {
  senate: { fill: '#ef4444', border: '#b91c1c' },
  house: { fill: '#3b82f6', border: '#1d4ed8' },
};

const DistrictMap = ({ coordinates, districts, isDarkMode }) => {
  const [activeLayer, setActiveLayer] = useState('senate');
  const [geoData, setGeoData] = useState({ senate: null, house: null });
  const [loading, setLoading] = useState(true);

  const senateDistrictNumber = districts?.state_senate?.district_number;
  const houseDistrictNumber = districts?.state_house?.district_number;

  // Load GeoJSON on mount
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    Promise.all([
      fetch('/geodata/ga-senate-districts.geojson').then(r => r.json()),
      fetch('/geodata/ga-house-districts.geojson').then(r => r.json()),
    ])
      .then(([senate, house]) => {
        if (!cancelled) setGeoData({ senate, house });
      })
      .catch(err => console.error('Failed to load district GeoJSON:', err))
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  // Filter to only the matching district
  const activeGeoJSON = useMemo(() => {
    const data = geoData[activeLayer];
    if (!data) return null;
    const districtNum = activeLayer === 'senate' ? senateDistrictNumber : houseDistrictNumber;
    if (!districtNum) return null;

    const key = activeLayer === 'senate' ? 'SLDUST' : 'SLDLST';
    const padded = String(districtNum).padStart(3, '0');

    return {
      type: 'FeatureCollection',
      features: data.features.filter(f => f.properties[key] === padded),
    };
  }, [geoData, activeLayer, senateDistrictNumber, houseDistrictNumber]);

  const colors = DISTRICT_COLORS[activeLayer];

  const tileUrl = isDarkMode
    ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
    : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
  const tileAttribution = isDarkMode
    ? '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>'
    : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

  if (!coordinates) return null;

  const geoJsonStyle = {
    color: colors.border,
    weight: 3,
    fillColor: colors.fill,
    fillOpacity: 0.15,
    dashArray: '5, 5',
  };

  return (
    <div className="w-full">
      {/* Senate / House toggle */}
      <div className="flex gap-2 mb-3">
        <button
          onClick={() => setActiveLayer('senate')}
          className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-all ${
            activeLayer === 'senate'
              ? 'bg-[#ef4444] text-white shadow-md'
              : isDarkMode
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Senate District{senateDistrictNumber ? ` ${senateDistrictNumber}` : ''}
        </button>
        <button
          onClick={() => setActiveLayer('house')}
          className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-all ${
            activeLayer === 'house'
              ? 'bg-[#3b82f6] text-white shadow-md'
              : isDarkMode
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          House District{houseDistrictNumber ? ` ${houseDistrictNumber}` : ''}
        </button>
      </div>

      {/* Map */}
      <div
        className={`rounded-lg overflow-hidden border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
        style={{ height: '400px', width: '100%' }}
      >
        {loading ? (
          <div className={`flex items-center justify-center h-full ${isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-50 text-gray-600'}`}>
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              <p className="text-sm">Loading district map…</p>
            </div>
          </div>
        ) : (
          <MapContainer
            center={[coordinates.lat, coordinates.lng]}
            zoom={11}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer url={tileUrl} attribution={tileAttribution} />

            {activeGeoJSON && activeGeoJSON.features.length > 0 && (
              <GeoJSON
                key={`${activeLayer}-${senateDistrictNumber}-${houseDistrictNumber}`}
                data={activeGeoJSON}
                style={geoJsonStyle}
              />
            )}

            <Marker position={[coordinates.lat, coordinates.lng]} />

            <FitBoundsController geoJsonData={activeGeoJSON} />
          </MapContainer>
        )}
      </div>

      {/* Legend */}
      <div className={`mt-2 flex items-center gap-4 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        <span className="flex items-center gap-1">
          <span
            style={{
              display: 'inline-block',
              width: 12,
              height: 12,
              border: `2px dashed ${colors.border}`,
              backgroundColor: `${colors.fill}33`,
              borderRadius: 2,
            }}
          ></span>
          {activeLayer === 'senate' ? 'Senate' : 'House'} District{' '}
          {activeLayer === 'senate' ? senateDistrictNumber : houseDistrictNumber}
        </span>
        <span>📍 Your address</span>
      </div>
    </div>
  );
};

export default DistrictMap;
